/* ════════════════════════════════════════════════════════════
   SUPERSYMMETRY — CART BEHAVIOUR (shared, global)
   The one DOM/interaction layer for the cart. Loaded globally with defer.

   Reads nothing about money — it asks theme.cart (cart-store.js) to mutate,
   then swaps the Liquid-rendered section HTML that comes back into the live
   drawer (#cart-drawer-inner) and, when present, the cart page (#cart-root).
   Because both surfaces render the same shared snippets, one delegated set of
   handlers drives them both — there is no second cart implementation.

   Responsibilities:
     • react to 'cart:updated' → swap section HTML, open the drawer on add
     • delegated quantity steppers, direct qty edits, remove, clear, promo
     • a single toast for success / error feedback
   ════════════════════════════════════════════════════════════ */
(function () {
  var busy = false;

  /* ── toast ── */
  function notify(msg) {
    var toast = document.getElementById('toast');
    var toastMsg = document.getElementById('toastMsg');
    if (!toast || !toastMsg || !msg) { return; }
    toastMsg.textContent = msg;
    toast.classList.add('is-on');
    clearTimeout(notify._t);
    notify._t = setTimeout(function () { toast.classList.remove('is-on'); }, 1900);
  }

  /* ── swap freshly-rendered section HTML into the live DOM ──
     Keeps the live <theme-drawer> element (already wired) and replaces only its
     inner, so an open drawer stays open and focus/scroll are preserved. */
  function applySections(sections) {
    if (!sections) { return; }
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (html == null) { return; }
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var freshSel = (id === 'cart-drawer') ? '#cart-drawer-inner' : '#cart-root';
      var fresh = tmp.querySelector(freshSel);
      var current = document.querySelector(freshSel);
      if (fresh && current) {
        /* The sheet's body scrolls; keep the shopper where they were (a tap on
           a gift colour or a scarf „+" further down used to jump to the top). */
        var oldBody = current.querySelector('[data-cart-body]');
        var y = oldBody ? oldBody.scrollTop : 0;
        current.replaceWith(fresh);
        var newBody = y && fresh.querySelector('[data-cart-body]');
        if (newBody) { newBody.scrollTop = y; }
      }
    });
  }

  document.addEventListener('cart:updated', function (e) {
    var d = e.detail || {};
    if (d.sections) { applySections(d.sections); initRails(); giftFix(); giftCue(false); }
    if (d.source === 'add') {
      var drawer = document.getElementById('cartDrawer');
      /* Only open a closed drawer. open() records document.activeElement as
         the thing to return focus to on close; an add from inside the open
         sheet (the gift, the scarf carousel) would make that a button the swap just removed,
         and the bag icon that really opened it would be forgotten. */
      if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('is-open')) { drawer.open(); }
    }
  });

  /* ── scarf carousel (snippets/cart-upsell.liquid) ──
     The row rolls slowly by itself. It is still an ordinary scroller (a finger
     swipes it, the arrows step it), so the roll is scrollLeft moved a fraction
     of a pixel per frame — never a transform that would fight the finger. To
     loop without a jump the cards are cloned once (aria-hidden, untabbable)
     and the position wraps by the width of one set.

     It stops whenever someone is using it — finger down, mouse over, keyboard
     focus inside — and picks up again a moment after; it never runs off
     screen (closed drawer, scrolled-away cart page, hidden tab) and never
     for prefers-reduced-motion. The rail is re-rendered on every cart swap,
     so where it had got to is carried across the swap. */
  var ROLL_SPEED = 16;                /* px per second — slow */
  var RESUME_AFTER = 2500;            /* ms after the shopper lets go */
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var railPos = 0;                    /* survives section swaps */

  function initRails() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-rail-track]'), function (track) {
      if (track.dataset.rollInit) { return; }
      track.dataset.rollInit = '1';
      var cards = Array.prototype.slice.call(track.children);
      if (!cards.length || track.scrollWidth <= track.clientWidth + 8) { return; }

      cards.forEach(function (li) {
        var c = li.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        Array.prototype.forEach.call(c.querySelectorAll('a, button'), function (el) { el.tabIndex = -1; });
        track.appendChild(c);
      });
      var setWidth = track.children[cards.length].offsetLeft - track.children[0].offsetLeft;
      var pos = setWidth ? railPos % setWidth : 0;
      track.scrollLeft = pos;

      var held = false, until = 0, visible = false, last = 0;
      var hold = function () { held = true; };
      var release = function () { held = false; until = Date.now() + RESUME_AFTER; };
      track.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { hold(); } });
      track.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { release(); } });
      track.addEventListener('touchstart', hold, { passive: true });
      track.addEventListener('touchend', release, { passive: true });
      track.addEventListener('touchcancel', release, { passive: true });
      track.addEventListener('focusin', hold);
      track.addEventListener('focusout', release);
      track._pause = function (ms) { until = Date.now() + (ms || RESUME_AFTER); };

      var running = false;
      function start() { if (!running && !reduceMotion) { running = true; last = 0; requestAnimationFrame(frame); } }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (en) { visible = en[0].isIntersecting; if (visible) { start(); } }).observe(track);
      } else { visible = true; start(); }

      /* Runs only while the row is on screen: off screen the loop ends and
         the observer starts it again. */
      function frame(t) {
        if (!track.isConnected || !visible) { running = false; return; }
        var dt = last ? Math.min(t - last, 64) : 0;
        last = t;
        if (!held && !document.hidden && Date.now() > until) {
          pos += ROLL_SPEED * dt / 1000;
          if (pos >= setWidth) { pos -= setWidth; }
          track.scrollLeft = pos;
        } else {
          /* someone moved it: follow, and keep the loop seamless */
          pos = track.scrollLeft;
          if (pos >= setWidth) { pos -= setWidth; track.scrollLeft = pos; }
        }
        railPos = pos;
        requestAnimationFrame(frame);
      }
    });
  }

  /* ── free gift (snippets/cart-gift.liquid) ──
     Shopify decides whether the gift is really free (an automatic discount),
     and when two discounts cannot stack it applies one. So the sheet checks the
     server's answer on every swap: a gift line that is under the spend, came
     back at full price, or is more than one is flagged data-gift-fix, and is
     removed (or set back to one) here with the reason as the toast. */
  function giftFix() {
    var fix = document.querySelector('[data-gift-fix]');
    if (!fix) { return; }
    var key = fix.dataset.giftKey, kind = fix.dataset.giftFix, msg = fix.dataset.giftMsg || '';
    if (!key) { return; }
    fix.removeAttribute('data-gift-fix'); /* once per swap — the next one is fresh HTML anyway */
    run(function () { return theme.cart.change(key, kind === 'qty' ? 1 : 0); }, msg);
  }

  /* The cue: when the gift becomes available — the bag crosses the spend
     while the sheet is open, or the sheet opens with a colour still to pick —
     „pasirinkite spalvą" thickens and the dots pop in turn (CSS .is-cue).
     On the change into „open" (or the sheet opening), then twice more while
     no colour is picked; never while it simply stays open after that. */
  var giftState = null;
  var cueTimer = null, cueLeft = 0;
  var CUE_REPEAT = 2, CUE_EVERY = 4500; /* the first play + two more, ~4.5s apart */
  function playCue(g) {
    g.classList.remove('is-cue');
    void g.offsetWidth;              /* restart the animation */
    g.classList.add('is-cue');
  }
  function stopCue() { clearInterval(cueTimer); cueTimer = null; cueLeft = 0; }
  function giftCue(force) {
    var g = document.querySelector('#cart-drawer-inner [data-gift]');
    var m = g && /gift-meter--(\w+)/.exec(g.className);
    var st = m ? m[1] : null;
    var drawer = document.getElementById('cartDrawer');
    var shown = drawer && drawer.classList.contains('is-open');
    if (st !== 'open') { stopCue(); }
    if (st === 'open' && shown && (force || giftState !== 'open')) {
      playCue(g);
      stopCue();
      cueLeft = CUE_REPEAT;
      /* Replays while the colours are still waiting — and only then: a closed
         sheet, a picked colour or a finger on the dots ends it. */
      cueTimer = setInterval(function () {
        var now = document.querySelector('#cart-drawer-inner [data-gift].gift-meter--open');
        if (!now || cueLeft <= 0 || !drawer.classList.contains('is-open') || now.matches(':hover, :focus-within')) { stopCue(); return; }
        cueLeft -= 1;
        playCue(now);
      }, CUE_EVERY);
    }
    giftState = st;
  }
  (function watchDrawer() {
    var drawer = document.getElementById('cartDrawer');
    if (!drawer || !('MutationObserver' in window)) { return; }
    var was = drawer.classList.contains('is-open');
    new MutationObserver(function () {
      var now = drawer.classList.contains('is-open');
      /* after the sheet has slid up (250ms), so the cue is seen */
      if (now && !was) { setTimeout(function () { giftCue(true); }, 300); }
      was = now;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  })();

  /* One tap on a colour adds the gift; a different colour swaps it. The old
     gift goes first so there is never a moment with two. */
  function giftChoose(pick) {
    var g = pick.closest('[data-gift]');
    if (!g || pick.getAttribute('aria-checked') === 'true') { return; }
    var current = g.querySelector('[data-gift-pick][aria-checked="true"]');
    var line = current && document.querySelector('.line[data-gift-line]');
    var add = function () {
      /* Kite's own marks — its discount only frees a line that carries them. */
      return theme.cart.add([{ id: pick.dataset.variantId, quantity: 1, properties: {
        _free_product: 'true',
        _rule_id: g.dataset.giftRule || 'free_gift_tier_0',
        _kite_promo_name: g.dataset.giftPromo || ''
      } }]);
    };
    run(function () {
      return (line ? theme.cart.change(line.dataset.key, 0) : Promise.resolve()).then(add);
    }, g.dataset.addedMsg || '');
  }

  /* ── one in-flight request at a time; the latest request waits its turn ──
     Every change is a ~600ms round trip to Shopify. A second tap inside that
     window used to be dropped on the floor — the busy guard returned and the
     shopper's „+ +" became „+". Now it queues (last one wins), and while a
     request is out the money on screen is marked pending (aria-busy on the
     swap target: prices, total and the checkout figure dim until the server
     answers — they are the parts that are about to change). */
  var queued = null;
  function pending(on) {
    Array.prototype.forEach.call(document.querySelectorAll('#cart-drawer-inner, #cart-root'), function (el) {
      if (on) { el.setAttribute('aria-busy', 'true'); } else { el.removeAttribute('aria-busy'); }
    });
  }
  function run(fn, okMsg) {
    if (busy) { queued = { fn: fn, okMsg: okMsg }; return; }
    busy = true;
    pending(true);
    fn()
      .then(function () { notify(okMsg); })
      .catch(function (err) {
        notify((err && err.userMessage) || theme.t('cart.update_error'));
        /* An optimistic quantity may be on screen — put the server's back. */
        if (theme.cart.sync) { return theme.cart.sync().catch(function () {}); }
      })
      .then(function () {
        busy = false;
        pending(false);
        if (queued) { var q = queued; queued = null; run(q.fn, q.okMsg); }
      });
  }

  /* ── delegated clicks (survive #cart-drawer-inner / #cart-root swaps) ── */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t.closest) { return; }

    var step = t.closest('[data-step]');
    if (step && step.closest('.qty')) {
      var line = step.closest('.line');
      if (!line) { return; }
      var input = line.querySelector('.qty input');
      var cur = parseInt(input ? input.value : '1', 10);
      if (isNaN(cur)) { cur = 1; }
      var next = cur + parseInt(step.dataset.step, 10);
      if (next < 1) { next = 1; }
      if (next === cur) { return; } /* minus at 1: no change, don't round-trip */
      /* The digit moves now; the money waits for Shopify. If the server
         refuses (stock cap), run() syncs the real quantity back. */
      if (input) { input.value = next; }
      var key = line.dataset.key;
      run(function () { return theme.cart.change(key, next); });
      return;
    }

    var rm = t.closest('.line__remove');
    if (rm) {
      var l = rm.closest('.line');
      if (l) { run(function () { return theme.cart.change(l.dataset.key, 0); }, theme.t('cart.removed')); }
      return;
    }

    if (t.closest('#clearBtn')) {
      run(function () { return theme.cart.clear(); });
      return;
    }

    var gpick = t.closest('[data-gift-pick]');
    if (gpick) { giftChoose(gpick); return; }

    var radd = t.closest('[data-rail-add]');
    if (radd) {
      run(function () { return theme.cart.add([{ id: radd.dataset.variantId, quantity: 1 }]); }, theme.t('cart.upsell_added'));
      return;
    }

    var rstep = t.closest('[data-rail-step]');
    if (rstep) {
      var track = rstep.closest('[data-rail]').querySelector('[data-rail-track]');
      if (track) {
        if (track._pause) { track._pause(4000); }
        track.scrollBy({ left: parseInt(rstep.dataset.railStep, 10) * track.clientWidth * 0.8, behavior: 'smooth' });
      }
      return;
    }

    if (t.closest('#promoBtn')) {
      /* Discount codes apply at CHECKOUT — hand off to Shopify's /discount route,
         which sets the code and returns to the cart. No client-side money faking. */
      var pi = document.getElementById('promoInput');
      var code = pi ? pi.value.trim() : '';
      if (code) {
        window.location.href = '/discount/' + encodeURIComponent(code) +
          '?redirect=' + encodeURIComponent(window.location.pathname);
      }
      return;
    }
  });

  /* ── direct quantity edits ── */
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t.matches || !t.matches('.qty input')) { return; }
    var line = t.closest('.line');
    if (!line) { return; }
    var q = parseInt(String(t.value).replace(/\D/g, ''), 10);
    if (isNaN(q) || q < 1) { q = 1; }
    run(function () { return theme.cart.change(line.dataset.key, q); });
  });

  initRails();
})();
